import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useDebounce from "../../../../hooks/useDebounce";
import {useFormik} from "formik";
import {provjeriNormativeArtikla, saveZapisiStavke} from "../../../../api/zapisiProizvodnje";
import notifyToast from "../../../../components/Toast/Toast";
import LoadingComponent from "../../../../components/LoadingComponent/LoadingComponent";
import QrCodeScanner from "../../../../components/QrCodeScanner/QrCodeScanner";
import clsx from "clsx";
import {fetchTable} from "../../../../api/tableRS";

interface ProfilacijaTrakeProps {
    id: any;
    brojNaloga: any;
    kolicinaNaloga: number;
}

/** Zebra + general cleanup */
const normalizeScan = (value: string) =>
    String(value ?? "")
        .replace(/[\r\n\t]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

/** Robust company match: "Alternativa", "Alternativa.", "Alternativa doo", "ALTERNATIVA   DOO" etc. */
const isCompanyAlternativa = (s: string) => {
    const cleaned = normalizeScan(s)
        .toLowerCase()
        .replace(/[^a-z]/g, ""); // remove spaces, dots, etc.

    return cleaned === "alternativa" || cleaned === "alternativadoo";
};

/**
 * ✅ Full traka QR payload (robust):
 * Example: "14; 41201; teteftad; Alternativa"  (can have trailing ';' or extra spaces)
 * - split by ';'
 * - trim + remove empty parts (handles trailing ';')
 * - need at least 4 parts
 * - last meaningful part must be Alternativa/Alternativa doo (robust compare)
 */
const isFullTrakaQr = (raw: string) => {
    const s = normalizeScan(raw);
    if (!s) return false;

    const parts = s
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);

    if (parts.length < 4) return false;

    const last = parts[parts.length - 1] ?? "";
    return isCompanyAlternativa(last);
};

/** If scanner provides only ID -> treat as scanned */
const isIdOnly = (raw: string) => /^\d+$/.test(normalizeScan(raw));

/**
 * Zebra keyboard-wedge scanning:
 * - characters arrive very quickly
 * - usually ends with Enter/Tab
 */
function useKeyboardScanner(onScan: (value: string) => void) {
    const bufferRef = useRef("");
    const lastTsRef = useRef<number>(0);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const ts = Date.now();
            const delta = ts - lastTsRef.current;
            lastTsRef.current = ts;

            // slow typing => manual => reset buffer
            if (delta > 60) bufferRef.current = "";

            // finish scan on Enter/Tab
            if (e.key === "Enter" || e.key === "Tab") {
                const buf = bufferRef.current;
                bufferRef.current = "";
                if (buf && buf.length >= 4) onScan(buf);
                return;
            }

            // ignore non-character keys
            if (e.key.length !== 1) return;

            bufferRef.current += e.key;
        };

        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onScan]);
}

const TRAKA_SCANNED_MSG = "Traka skenirana";
const TRAKA_NOT_SCANNED_MSG =
    "Traka nije skenirana! Molimo skenirajte QR kod. Skeniranje trake se prati i bice vezano za vas racun!";

const ProfilacijaTrake: React.FC<ProfilacijaTrakeProps> = ({id, brojNaloga, kolicinaNaloga}) => {
    const initialValues = {
        "TRAKA-G": "",
        "POTVRDJUJEM-KORISTENJE-TRAKE-G": "",
        "TRAKA-D": "",
        "POTVRDJUJEM-KORISTENJE-TRAKE-D": "",

        // hidden meta (1/0)
        "TRAKA-G-SKENIRANA": 0,
        "TRAKA-D-SKENIRANA": 0,
    };

    const [loading, setLoading] = useState(false);

    const [trakaGId, setTrakaGId] = useState("");
    const debouncedTrakaGId = useDebounce(trakaGId);
    const [trakaGDetalji, setTrakaGDetalji] = useState("");

    const [trakaDId, setTrakaDId] = useState("");
    const debouncedTrakaDId = useDebounce(trakaDId);
    const [trakaDDetalji, setTrakaDDetalji] = useState("");

    // raw scan payload (scanner only OR full-QR content detected)
    const [trakaGRawScan, setTrakaGRawScan] = useState("");
    const [trakaDRawScan, setTrakaDRawScan] = useState("");

    // marks that scan came from scanner (QrCodeScanner / Zebra wedge / full-QR content)
    const [trakaGScannedByDevice, setTrakaGScannedByDevice] = useState(false);
    const [trakaDScannedByDevice, setTrakaDScannedByDevice] = useState(false);

    // show status only after interaction
    const [trakaGInteracted, setTrakaGInteracted] = useState(false);
    const [trakaDInteracted, setTrakaDInteracted] = useState(false);

    // values from API for calculations
    const [tolerancijaValjanjaGornjeTrake, setTolerancijaValjanjaGornjeTrake] = useState<number>(0);
    const [tolerancijaValjanjaDonjeTrake, setTolerancijaValjanjaDonjeTrake] = useState<number>(0);

    // normative loading
    const [normativeLoading, setNormativeLoading] = useState(false);
    const [nalogData, setNalogData] = useState<any>(null);

    const debljinaGore = nalogData?.debljina_gore?.debljina ?? "";
    const debljinaDole = nalogData?.debljina_dole?.debljina ?? "";

    // last focused input fallback (helps if activeElement is weird on device)
    const lastFocusedRef = useRef<"G" | "D">("G");

    // optional: auto-focus first input to reduce “must click”
    const trakaGInputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        trakaGInputRef.current?.focus();
    }, []);

    // ---------------- Formik ----------------
    const formik = useFormik({
        initialValues,
        onSubmit: async (values, {setStatus, setSubmitting, resetForm}) => {
            setLoading(true);
            try {
                const data: {IdZapisa: any; BrojNaloga: any; MetaKey: string; MetaValue: any}[] = [];

                Object.entries(values).forEach(([key, value]) => {
                    const isEmpty = value === "" || value === null || value === undefined;
                    if (!isEmpty) {
                        data.push({
                            IdZapisa: id,
                            BrojNaloga: brojNaloga,
                            MetaKey: key,
                            MetaValue: value,
                        });
                    }
                });

                const response = await saveZapisiStavke(data);
                const responseData = response;

                if (responseData.error === 0) {
                    notifyToast(true, responseData.message);

                    resetForm();

                    setTrakaGDetalji("");
                    setTrakaDDetalji("");

                    setTrakaGRawScan("");
                    setTrakaDRawScan("");
                    setTrakaGScannedByDevice(false);
                    setTrakaDScannedByDevice(false);

                    setTrakaGInteracted(false);
                    setTrakaDInteracted(false);

                    setTrakaGId("");
                    setTrakaDId("");

                    setTolerancijaValjanjaGornjeTrake(0);
                    setTolerancijaValjanjaDonjeTrake(0);
                } else {
                    notifyToast(false, responseData.message || "An unexpected error occurred.");
                }
            } catch (error: any) {
                notifyToast(false, error?.response?.data?.message);
                setStatus(error);
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        },
    });

    // ---------------- Normatives ----------------
    useEffect(() => {
        if (!id && !brojNaloga) return;

        const run = async () => {
            setNormativeLoading(true);
            try {
                const params = {idZapisa: id, brojNaloga};
                const res = await provjeriNormativeArtikla(params);
                const payload = res?.data;

                if (payload?.error === 0 && payload?.data?.nalogData) {
                    setNalogData(payload.data.nalogData);
                } else {
                    setNalogData(null);
                }
            } catch {
                setNalogData(null);
            } finally {
                setNormativeLoading(false);
            }
        };

        run();
    }, [id, brojNaloga]);

    // ---------------- Scanner apply helpers ----------------
    const applyTrakaGScan = useCallback(
        (raw: string) => {
            const cleaned = normalizeScan(raw);

            setTrakaGScannedByDevice(true);
            setTrakaGRawScan(cleaned);
            setTrakaGInteracted(true);

            const idOnly = (cleaned.split(";")[0] ?? "").trim() || cleaned;
            setTrakaGId(idOnly);
            formik.setFieldValue("TRAKA-G", idOnly);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const applyTrakaDScan = useCallback(
        (raw: string) => {
            const cleaned = normalizeScan(raw);

            setTrakaDScannedByDevice(true);
            setTrakaDRawScan(cleaned);
            setTrakaDInteracted(true);

            const idOnly = (cleaned.split(";")[0] ?? "").trim() || cleaned;
            setTrakaDId(idOnly);
            formik.setFieldValue("TRAKA-D", idOnly);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // Zebra wedge scans: route to focused input, else last-focused fallback
    useKeyboardScanner((value) => {
        const active = document.activeElement as HTMLElement | null;

        if (active?.id === "TRAKA-G") return applyTrakaGScan(value);
        if (active?.id === "TRAKA-D") return applyTrakaDScan(value);

        if (lastFocusedRef.current === "G") return applyTrakaGScan(value);
        return applyTrakaDScan(value);
    });

    // ✅ computed scan flags (ONLY true if came from scanner)
    const trakaGSkenirana = useMemo(() => {
        if (!trakaGScannedByDevice) return false; // manual => never scanned
        return isIdOnly(trakaGRawScan) || isFullTrakaQr(trakaGRawScan);
    }, [trakaGRawScan, trakaGScannedByDevice]);

    const trakaDSkenirana = useMemo(() => {
        if (!trakaDScannedByDevice) return false; // manual => never scanned
        return isIdOnly(trakaDRawScan) || isFullTrakaQr(trakaDRawScan);
    }, [trakaDRawScan, trakaDScannedByDevice]);

    // ✅ sync hidden form fields
    useEffect(() => {
        formik.setFieldValue("TRAKA-G-SKENIRANA", trakaGSkenirana ? 1 : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trakaGSkenirana]);

    useEffect(() => {
        formik.setFieldValue("TRAKA-D-SKENIRANA", trakaDSkenirana ? 1 : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trakaDSkenirana]);

    // ---------------- Fetch table: G ----------------
    useEffect(() => {
        if (debouncedTrakaGId !== "") {
            fetchTable(debouncedTrakaGId)
                .then((response) => {
                    if (response?.Id) {
                        const trakaDebljina = String(response?.repromaterijal?.ReprMatDebljina ?? "").trim();
                        const requiredDebljina = String(debljinaGore ?? "").trim();

                        if (trakaDebljina && requiredDebljina && trakaDebljina !== requiredDebljina) {
                            alert(
                                `⚠️ NEODGOVARAJUĆA DEBLJINA – GORNJA TRAKA\n\n` +
                                `Potrebna debljina: ${requiredDebljina} mm\n` +
                                `Debljina trake: ${trakaDebljina} mm`
                            );
                        }

                        const msg = `Traka: ${response.Id} | Težina: ${response.Tezina} | Širina: ${
                            response.Sirina
                        } | Debljina: ${response.repromaterijal?.ReprMatDebljina || "N/A"} | Proizvođač: ${
                            response.repromaterijal?.proizvodjac?.PartnerNaziv || "N/A"
                        } | Boja: ${response.repromaterijal?.boja_lica?.BojaNaziv || "N/A"} | Šarža: ${
                            response.repromaterijal?.stavka?.ugovor?.BrojUgovora ?? "N/A"
                        } / ${response.repromaterijal?.stavka?.ManualId ?? "N/A"}`;

                        setTolerancijaValjanjaGornjeTrake(Number(response?.TolerancijaValjanja ?? 0));
                        setTrakaGDetalji(msg);
                    } else {
                        setTrakaGDetalji("Greška: traka nije pronađena.");
                    }
                })
                .catch((error) => notifyToast(false, error?.message));
        } else {
            setTrakaGDetalji("");
            setTolerancijaValjanjaGornjeTrake(0);
        }
    }, [debouncedTrakaGId, debljinaGore]);

    // ---------------- Fetch table: D ----------------
    useEffect(() => {
        if (debouncedTrakaDId !== "") {
            fetchTable(debouncedTrakaDId)
                .then((response) => {
                    if (response?.Id) {
                        const trakaDebljina = String(response?.repromaterijal?.ReprMatDebljina ?? "").trim();
                        const requiredDebljina = String(debljinaDole ?? "").trim();

                        if (trakaDebljina && requiredDebljina && trakaDebljina !== requiredDebljina) {
                            alert(
                                `⚠️ NEODGOVARAJUĆA DEBLJINA – DONJA TRAKA\n\n` +
                                `Potrebna debljina: ${requiredDebljina} mm\n` +
                                `Debljina trake: ${trakaDebljina} mm`
                            );
                        }

                        const msg = `Traka: ${response.Id} | Težina: ${response.Tezina} | Širina: ${
                            response.Sirina
                        } | Debljina: ${response.repromaterijal?.ReprMatDebljina || "N/A"} | Proizvođač: ${
                            response.repromaterijal?.proizvodjac?.PartnerNaziv || "N/A"
                        } | Boja: ${response.repromaterijal?.boja_lica?.BojaNaziv || "N/A"} | Šarža: ${
                            response.repromaterijal?.stavka?.ugovor?.BrojUgovora ?? "N/A"
                        } / ${response.repromaterijal?.stavka?.ManualId ?? "N/A"}`;

                        setTolerancijaValjanjaDonjeTrake(Number(response?.TolerancijaValjanja ?? 0));
                        setTrakaDDetalji(msg);
                    } else {
                        setTrakaDDetalji("Greška: traka nije pronađena.");
                    }
                })
                .catch((error) => notifyToast(false, error?.message));
        } else {
            setTrakaDDetalji("");
            setTolerancijaValjanjaDonjeTrake(0);
        }
    }, [debouncedTrakaDId, debljinaDole]);

    // ✅ Needed quantities (kolicinaNaloga * tolerancija)
    const potrebnaKolicinaGornja = useMemo(() => {
        if (!kolicinaNaloga || !tolerancijaValjanjaGornjeTrake) return 0;
        return Math.ceil(Number(kolicinaNaloga) * Number(tolerancijaValjanjaGornjeTrake));
    }, [kolicinaNaloga, tolerancijaValjanjaGornjeTrake]);

    const potrebnaKolicinaDonja = useMemo(() => {
        if (!kolicinaNaloga || !tolerancijaValjanjaDonjeTrake) return 0;
        return Math.ceil(Number(kolicinaNaloga) * Number(tolerancijaValjanjaDonjeTrake));
    }, [kolicinaNaloga, tolerancijaValjanjaDonjeTrake]);

    // ---------------- UI blocks ----------------
    const ThicknessBanner = () => (
        <div
            className={clsx("alert mb-4 mt-6 d-flex align-items-center justify-content-between flex-wrap gap-2", {
                "alert-secondary": normativeLoading,
                "alert-info": !normativeLoading && (debljinaGore || debljinaDole),
                "alert-warning": !normativeLoading && !debljinaGore && !debljinaDole,
            })}
            role="alert"
        >
            {normativeLoading ? (
                <div className="d-flex align-items-center gap-2">
                    <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    <span>Učitavam debljine...</span>
                </div>
            ) : debljinaGore || debljinaDole ? (
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <strong>Debljine potrebno koristiti:</strong>
                    <span className="badge bg-light text-dark border">
            Gore: <strong>{debljinaGore || "N/A"} mm</strong>
          </span>
                    <span className="badge bg-light text-dark border">
            Dole: <strong>{debljinaDole || "N/A"} mm</strong>
          </span>
                </div>
            ) : (
                <div>
                    <strong>Debljine potrebno koristiti:</strong> nema podataka za ovaj nalog.
                </div>
            )}
        </div>
    );

    const ScanStatus = ({interacted, scanned}: {interacted: boolean; scanned: boolean}) => {
        if (!interacted) return null;
        return scanned ? (
            <div className="alert alert-success py-2 mb-0 d-flex align-items-center justify-content-between">
                <span className="fw-semibold">{TRAKA_SCANNED_MSG}</span>
                <span className="badge bg-success">OK</span>
            </div>
        ) : (
            <div className="alert alert-danger py-2 mb-0">
                <div className="fw-semibold mb-1">Traka nije skenirana</div>
                <div className="small">{TRAKA_NOT_SCANNED_MSG}</div>
            </div>
        );
    };

    return (
        <div className="container-fluid">
            {loading ? (
                <LoadingComponent />
            ) : (
                <form onSubmit={formik.handleSubmit}>
                    <ThicknessBanner />

                    <div className="row g-4" id="profilacija-trake">
                        {/* ===================== TRAKA G ===================== */}
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-primary">G</span>
                                        <h5 className="mb-0">Gornja traka (lice)</h5>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        {trakaGInteracted ? (
                                            trakaGSkenirana ? (
                                                <span className="badge bg-success">Skenirana</span>
                                            ) : (
                                                <span className="badge bg-danger">Nije skenirana</span>
                                            )
                                        ) : (
                                            <span className="badge bg-light text-dark border">Nije provjereno</span>
                                        )}
                                    </div>
                                </div>

                                <div className="card-body">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-12 col-md-2">
                                            <div className="d-flex flex-column gap-2">
                                                <div className="small text-muted fw-semibold">Skeniraj QR kamerom</div>
                                                <QrCodeScanner
                                                    onScan={(data) => {
                                                        // scanner event => scanned = true (even if only ID)
                                                        applyTrakaGScan(data);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-4">
                                            <label className="form-label fw-bold">TRAKA G</label>
                                            <input
                                                ref={trakaGInputRef}
                                                type="text"
                                                className="form-control"
                                                id="TRAKA-G"
                                                {...formik.getFieldProps("TRAKA-G")}
                                                placeholder="Upišite broj trake"
                                                onFocus={() => (lastFocusedRef.current = "G")}
                                                onChange={(e) => {
                                                    setTrakaGInteracted(true);

                                                    const raw = e.target.value;
                                                    const cleaned = normalizeScan(raw);

                                                    // ✅ If Zebra (or paste) produced full QR -> mark as scanned automatically
                                                    if (isFullTrakaQr(cleaned)) {
                                                        setTrakaGScannedByDevice(true);
                                                        setTrakaGRawScan(cleaned);

                                                        const idOnly = (cleaned.split(";")[0] ?? "").trim();
                                                        setTrakaGId(idOnly);
                                                        formik.setFieldValue("TRAKA-G", idOnly);
                                                        return;
                                                    }

                                                    // manual/partial typing => NOT scanned
                                                    setTrakaGScannedByDevice(false);
                                                    setTrakaGRawScan("");

                                                    setTrakaGId(raw);
                                                    formik.setFieldValue("TRAKA-G", raw);
                                                }}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold">POTVRĐUJEM KORIŠTENJE OVE TRAKE</label>
                                            <select className="form-select" {...formik.getFieldProps("POTVRDJUJEM-KORISTENJE-TRAKE-G")}>
                                                <option value="">ODABERI</option>
                                                <option value="DA">DA</option>
                                                <option value="NE">NE</option>
                                            </select>
                                        </div>

                                        <input
                                            type="hidden"
                                            id="TRAKA-G-SKENIRANA"
                                            name="TRAKA-G-SKENIRANA"
                                            value={formik.values["TRAKA-G-SKENIRANA"] as any}
                                        />
                                    </div>

                                    <div className="row g-3 mt-2">
                                        <div className="col-12 col-lg-8">
                                            <div className={clsx("p-3 rounded border", {"bg-light": !!trakaGDetalji})}>
                                                <div className="small text-muted fw-semibold mb-1">Detalji trake</div>
                                                <div className="fw-semibold">{trakaGDetalji || "—"}</div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-lg-4 d-flex flex-column gap-2">
                                            <ScanStatus interacted={trakaGInteracted} scanned={trakaGSkenirana} />

                                            {Number(potrebnaKolicinaGornja) > 0 && (
                                                <div className="alert alert-warning py-2 mb-0">
                                                    <div className="fw-semibold">Potrebna količina materijala za nalog</div>
                                                    <div className="fs-5">
                                                        <strong>{potrebnaKolicinaGornja}</strong> kg
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Debug (optional):
                  <div className="mt-2 small text-muted">RAW G: {trakaGRawScan || "(empty)"}</div>
                  */}
                                </div>
                            </div>
                        </div>

                        {/* ===================== TRAKA D ===================== */}
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header bg-white d-flex align-items-center justify-content-between flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-primary">D</span>
                                        <h5 className="mb-0">Donja traka (nalicje)</h5>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        {trakaDInteracted ? (
                                            trakaDSkenirana ? (
                                                <span className="badge bg-success">Skenirana</span>
                                            ) : (
                                                <span className="badge bg-danger">Nije skenirana</span>
                                            )
                                        ) : (
                                            <span className="badge bg-light text-dark border">Nije provjereno</span>
                                        )}
                                    </div>
                                </div>

                                <div className="card-body">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-12 col-md-2">
                                            <div className="d-flex flex-column gap-2">
                                                <div className="small text-muted fw-semibold">Skeniraj QR kamerom</div>
                                                <QrCodeScanner
                                                    onScan={(data) => {
                                                        applyTrakaDScan(data);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-4">
                                            <label className="form-label fw-bold">TRAKA D</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="TRAKA-D"
                                                {...formik.getFieldProps("TRAKA-D")}
                                                placeholder="Upišite broj trake"
                                                onFocus={() => (lastFocusedRef.current = "D")}
                                                onChange={(e) => {
                                                    setTrakaDInteracted(true);

                                                    const raw = e.target.value;
                                                    const cleaned = normalizeScan(raw);

                                                    // ✅ If Zebra produced full QR -> scanned
                                                    if (isFullTrakaQr(cleaned)) {
                                                        setTrakaDScannedByDevice(true);
                                                        setTrakaDRawScan(cleaned);

                                                        const idOnly = (cleaned.split(";")[0] ?? "").trim();
                                                        setTrakaDId(idOnly);
                                                        formik.setFieldValue("TRAKA-D", idOnly);
                                                        return;
                                                    }

                                                    setTrakaDScannedByDevice(false);
                                                    setTrakaDRawScan("");

                                                    setTrakaDId(raw);
                                                    formik.setFieldValue("TRAKA-D", raw);
                                                }}
                                            />
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-bold">POTVRĐUJEM KORIŠTENJE OVE TRAKE</label>
                                            <select className="form-select" {...formik.getFieldProps("POTVRDJUJEM-KORISTENJE-TRAKE-D")}>
                                                <option value="">ODABERI</option>
                                                <option value="DA">DA</option>
                                                <option value="NE">NE</option>
                                            </select>
                                        </div>

                                        <input
                                            type="hidden"
                                            id="TRAKA-D-SKENIRANA"
                                            name="TRAKA-D-SKENIRANA"
                                            value={formik.values["TRAKA-D-SKENIRANA"] as any}
                                        />
                                    </div>

                                    <div className="row g-3 mt-2">
                                        <div className="col-12 col-lg-8">
                                            <div className={clsx("p-3 rounded border", {"bg-light": !!trakaDDetalji})}>
                                                <div className="small text-muted fw-semibold mb-1">Detalji trake</div>
                                                <div className="fw-semibold">{trakaDDetalji || "—"}</div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-lg-4 d-flex flex-column gap-2">
                                            <ScanStatus interacted={trakaDInteracted} scanned={trakaDSkenirana} />

                                            {Number(potrebnaKolicinaDonja) > 0 && (
                                                <div className="alert alert-warning py-2 mb-0">
                                                    <div className="fw-semibold">Potrebna količina materijala za nalog</div>
                                                    <div className="fs-5">
                                                        <strong>{potrebnaKolicinaDonja}</strong> kg
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Debug (optional):
                  <div className="mt-2 small text-muted">RAW D: {trakaDRawScan || "(empty)"}</div>
                  */}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <button type="submit" className="btn btn-primary px-4">
                            Potvrdi
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default ProfilacijaTrake;
